/* eslint-disable @typescript-eslint/no-explicit-any */

import { isServer } from '@vue-storefront/core/helpers'

// Drop-in replacement for the small slice of the `localforage` API that VSF
// Core actually uses. Every cache collection in `config/default.json` is
// pinned to the LOCALSTORAGE driver, so the real runtime path was always
// `window.localStorage` wrapped in promise-shaped sugar. This module mirrors
// that surface (and the on-disk key/value format) without pulling the ~92 KB
// localforage module that also ships unused IndexedDB / WebSQL adapters.
//
// On-disk compatibility:
//  - Keys are stored under `${name}/${storeName}/${key}` so existing entries
//    written by the previous localforage-backed cache continue to read back.
//    (localforage's LOCALSTORAGE driver drops the `${storeName}/` segment when
//    `storeName === 'keyvaluepairs'`, which VSF never uses; we mirror that
//    edge case anyway for parity.)
//  - Values are `JSON.stringify`d. VSF only ever stored JSON-serializable
//    data, so the encoded form is byte-for-byte identical to what
//    localforage emitted for the same input.

export const LOCALSTORAGE = 'localStorageWrapper'
export const INDEXEDDB = LOCALSTORAGE
export const WEBSQL = LOCALSTORAGE

const DEFAULT_NAME = 'localforage'
const DEFAULT_STORE_NAME = 'keyvaluepairs'

interface InstanceConfig {
  name?: string
  storeName?: string
  driver?: any
  version?: number
}

type IterateFn = (value: any, key: string, n: number) => any | void
type Cb<T> = (err: Error | null, result: T | null) => void

class LocalStorageCollection {
  public _config: Required<InstanceConfig>
  public _dbInfo: { version: number }
  private _keyPrefix: string

  constructor (config: InstanceConfig = {}) {
    const merged: Required<InstanceConfig> = {
      name: config.name || DEFAULT_NAME,
      storeName: config.storeName || DEFAULT_STORE_NAME,
      driver: config.driver || LOCALSTORAGE,
      version: config.version || 1
    }
    this._config = merged
    this._dbInfo = { version: merged.version }
    this._keyPrefix = merged.storeName === DEFAULT_STORE_NAME
      ? `${merged.name}/`
      : `${merged.name}/${merged.storeName}/`
  }

  ready (): Promise<void> {
    return Promise.resolve()
  }

  private _ls (): Storage | null {
    if (isServer) return null
    try {
      // Accessing localStorage can throw in some embedded / restricted
      // contexts (e.g. iframes with cookies disabled), hence the guard.
      return typeof window !== 'undefined' ? window.localStorage : null
    } catch (e) {
      return null
    }
  }

  getItem (key: string, cb?: Cb<any>): Promise<any> {
    return this.ready().then(() => {
      const ls = this._ls()
      if (!ls) {
        if (cb) cb(null, null)
        return null
      }
      const raw = ls.getItem(this._keyPrefix + key)
      if (raw === null) {
        if (cb) cb(null, null)
        return null
      }
      try {
        const v = JSON.parse(raw)
        if (cb) cb(null, v)
        return v
      } catch (e) {
        const err = e as Error
        if (cb) cb(err, null)
        return null
      }
    })
  }

  setItem (key: string, value: any, cb?: Cb<any>): Promise<any> {
    return this.ready().then(() => {
      const ls = this._ls()
      if (!ls) {
        if (cb) cb(null, value)
        return value
      }
      const serialized = JSON.stringify(value)
      try {
        ls.setItem(this._keyPrefix + key, serialized)
      } catch (e) {
        // Surface QuotaExceededError / DOMExceptions as rejected promises so
        // callers (notably LocalForageCacheDriver) can react to them.
        const err = e as Error
        if (cb) cb(err, null)
        throw err
      }
      if (cb) cb(null, value)
      return value
    })
  }

  removeItem (key: string, cb?: Cb<void>): Promise<void> {
    return this.ready().then(() => {
      const ls = this._ls()
      if (ls) ls.removeItem(this._keyPrefix + key)
      if (cb) cb(null, null)
    })
  }

  clear (cb?: Cb<void>): Promise<void> {
    return this.ready().then(() => {
      const ls = this._ls()
      if (ls) {
        const prefix = this._keyPrefix
        for (let i = ls.length - 1; i >= 0; i--) {
          const k = ls.key(i)
          if (k && k.indexOf(prefix) === 0) ls.removeItem(k)
        }
      }
      if (cb) cb(null, null)
    })
  }

  length (cb?: Cb<number>): Promise<number> {
    return this.keys().then(keys => {
      if (cb) cb(null, keys.length)
      return keys.length
    })
  }

  keys (cb?: Cb<string[]>): Promise<string[]> {
    return this.ready().then(() => {
      const out: string[] = []
      const ls = this._ls()
      if (!ls) {
        if (cb) cb(null, out)
        return out
      }
      const prefix = this._keyPrefix
      const len = ls.length
      for (let i = 0; i < len; i++) {
        const k = ls.key(i)
        if (k && k.indexOf(prefix) === 0) {
          out.push(k.substring(prefix.length))
        }
      }
      if (cb) cb(null, out)
      return out
    })
  }

  key (n: number, cb?: Cb<string | null>): Promise<string | null> {
    return this.keys().then(keys => {
      const k = n >= 0 && n < keys.length ? keys[n] : null
      if (cb) cb(null, k)
      return k
    })
  }

  iterate<T> (iterator: IterateFn, cb?: Cb<T | undefined>): Promise<T | undefined> {
    return this.ready().then(() => {
      const ls = this._ls()
      if (!ls) {
        if (cb) cb(null, undefined)
        return undefined
      }
      const prefix = this._keyPrefix
      // Snapshot keys first so the iterator callback can safely mutate
      // localStorage (e.g. via `removeItem`) without skewing the loop.
      const fullKeys: string[] = []
      const len = ls.length
      for (let i = 0; i < len; i++) {
        const k = ls.key(i)
        if (k && k.indexOf(prefix) === 0) fullKeys.push(k)
      }
      let n = 1
      let lastResult: any
      for (const fullKey of fullKeys) {
        const shortKey = fullKey.substring(prefix.length)
        const raw = ls.getItem(fullKey)
        let value: any = null
        if (raw !== null) {
          try { value = JSON.parse(raw) } catch (e) { value = null }
        }
        const ret = iterator(value, shortKey, n++)
        if (ret !== undefined) {
          lastResult = ret
          break
        }
      }
      if (cb) cb(null, lastResult)
      return lastResult
    })
  }
}

export function createInstance (config: InstanceConfig = {}): LocalStorageCollection {
  return new LocalStorageCollection(config)
}

export default {
  LOCALSTORAGE,
  INDEXEDDB,
  WEBSQL,
  createInstance
}
